import { useTheme } from '@mui/material/styles';

/**
 * Custom hook to get commonly used theme values
 * This ensures consistent theme usage across all components
 */
export const useAppTheme = () => {
  const theme = useTheme();
  
  return {
    // Original MUI theme
    muiTheme: theme,
    
    // Commonly used values for easy access
    colors: {
      background: theme.palette.background.default,
      surface: theme.palette.background.paper,
      text: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      primary: theme.palette.primary.main,
      secondary: theme.palette.secondary.main,
      success: theme.palette.success.main,
      warning: theme.palette.warning.main,
      error: theme.palette.error.main,
      info: theme.palette.info.main,
      divider: theme.palette.divider,
    },
    
    // Spacing helpers
    spacing: theme.spacing,
    
    // Typography helpers
    typography: theme.typography,
    
    // Shape helpers
    borderRadius: theme.shape.borderRadius,
    
    // Breakpoints
    breakpoints: theme.breakpoints,
    
    // Check if dark mode
    isDark: theme.palette.mode === 'dark',
    
    // Common alpha function for transparency
    alpha: (color, opacity) => theme.palette.augmentColor({
      color: { main: color }
    }).main + Math.round(opacity * 255).toString(16).padStart(2, '0'),
  };
};

/**
 * Get theme-aware colors for custom components
 * Use this when you need to create theme objects for non-MUI components
 */
export const getThemeColors = (theme) => ({
  background: theme.palette.background.default,
  surface: theme.palette.background.paper,
  text: theme.palette.text.primary,
  textSecondary: theme.palette.text.secondary,
  accent: theme.palette.primary.main,
  success: theme.palette.success.main,
  warning: theme.palette.warning.main,
  error: theme.palette.error.main,
  info: theme.palette.info.main,
});

/**
 * Common theme-based styles
 */
export const getCommonStyles = (theme) => ({
  // Card/Paper styles
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  
  // Button styles
  button: {
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none',
    fontWeight: 600,
  },
  
  // Input styles
  input: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
  
  // Toolbar styles
  toolbar: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
  },
});
